import { type ClinicClock, pickedDay } from "@/lib/clinic-clock";
import { getTimezone } from "@/lib/timezone";
import {
  zonedDateKey,
  zonedDayEnd,
  zonedInstantOnDay,
  zonedWallMinutes,
} from "@/lib/timezone-dates";

import type {
  MeasurementDataType,
  MeasurementField,
  MeasurementGoal,
  MeasurementGroupField,
  MeasurementGroupWithFields,
  MeasurementValueSummary,
} from "@/types/measurement";

/**
 * The day picked in `picked` (a picker marker) at `timeSource`'s time of day,
 * both on the clinic's clock (`timeZone`, the effective zone by default).
 *
 * SmartDateInput yields dates at 00:00 local (calendar click, blur-reparse).
 * Storing that directly makes a second same-day recording collide with an
 * earlier non-midnight timestamp. Merging preserves the recording moment.
 */
export function mergeDateKeepingTime(
  picked: Date,
  timeSource: Date,
  timeZone: string = getTimezone(),
): Date {
  const secondsAndMs = ((timeSource.getTime() % 60_000) + 60_000) % 60_000;
  const merged = zonedInstantOnDay(
    picked,
    zonedWallMinutes(timeSource, timeZone),
    timeZone,
  );
  return new Date(merged.getTime() + secondsAndMs);
}

const BUMP_AFTER_EXISTING_MS = 60_000; // 1 minute

/**
 * Compute the `measurement_date` to stamp on a batch of values being recorded.
 *
 * Rules (matches the intent discussed for ENG-2244):
 * - Selected date is today → use the current moment verbatim. Two same-day
 *   recordings naturally get distinct timestamps separated by real elapsed
 *   wall-clock time, so latest-wins dedup picks the newer one.
 * - Selected date is in the past/future, no existing values on that day →
 *   stamp selected Y-M-D with the current time-of-day (matches the old
 *   DatePicker + showTimePicker default when the user didn't touch time).
 * - Selected date is in the past/future, with existing values on that day →
 *   stamp selected Y-M-D with (latest existing time + 1 minute), capped to
 *   23:59:59.999 of the selected local day. Keeps the new value strictly
 *   after the prior one without rolling into the next day.
 *
 * `now` is an injectable clock for tests; defaults to `new Date()`.
 */
export function computeSaveTimestamp(
  selectedDate: Date,
  existingMeasurementDates: ReadonlyArray<string | Date>,
  now: Date = new Date(),
  timeZone: string = getTimezone(),
): Date {
  const selectedDay = pickedDay(selectedDate);
  if (zonedDateKey(now, timeZone) === selectedDay) {
    return new Date(now);
  }

  // Only consider entries on the selected local day. Callers typically pass
  // same-day data from a prefill query, but a stale cache or a misuse of the
  // helper elsewhere could leak cross-day entries — which would land the
  // stamp on the wrong day (cap engages against selectedDate, not against
  // the bumped Date's own day).
  let latest: Date | null = null;
  for (const raw of existingMeasurementDates) {
    const d = raw instanceof Date ? raw : new Date(raw);
    if (isNaN(d.getTime())) continue;
    if (zonedDateKey(d, timeZone) !== selectedDay) continue;
    if (!latest || d.getTime() > latest.getTime()) {
      latest = d;
    }
  }

  if (latest) {
    const bumped = new Date(latest.getTime() + BUMP_AFTER_EXISTING_MS);
    const endOfDay = zonedDayEnd(selectedDate, timeZone);
    return bumped.getTime() > endOfDay.getTime() ? endOfDay : bumped;
  }

  return mergeDateKeepingTime(selectedDate, now, timeZone);
}

/**
 * Data types that represent numeric measurements.
 */
export const NUMERIC_DATA_TYPES = ["FLOAT", "INTEGER"] as const;

export function isNumericDataType(dataType: string): boolean {
  return (NUMERIC_DATA_TYPES as readonly string[]).includes(dataType);
}

/**
 * Filter a list of fields down to those eligible for charting. Mirrors the
 * filter that the chart renderer applies at draw-time so editors don't let
 * users pick fields that would silently drop out of their panels.
 */
export function filterChartableFields<
  T extends { dataType: MeasurementDataType },
>(fields: T[]): T[] {
  return fields.filter((f) => isNumericDataType(f.dataType));
}

/**
 * Check if a measurement field represents height (stored in inches).
 */
export function isHeightField(field: MeasurementField): boolean {
  const unit = field.unit?.toLowerCase().trim();
  return (
    (unit === "in" || unit === "inches") &&
    field.name.toLowerCase().includes("height")
  );
}

export function roundToQuarterInch(inches: number): number {
  return Math.round(inches * 4) / 4;
}

/**
 * Convert total inches to feet and remaining inches, to the nearest quarter inch.
 */
export function inchesToFeetInches(totalInches: number): {
  feet: number;
  inches: number;
} {
  const rounded = roundToQuarterInch(totalInches);
  return { feet: Math.floor(rounded / 12), inches: rounded % 12 };
}

/**
 * Convert feet + inches to total inches.
 */
export function feetInchesToTotalInches(feet: number, inches: number): number {
  return feet * 12 + inches;
}

/**
 * Format total inches as a height string like 5'11", 5'6.5" or 6'.
 */
export function formatHeightDisplay(totalInches: number): string {
  const { feet, inches } = inchesToFeetInches(totalInches);
  if (inches === 0) return `${feet}'`;
  return `${feet}'${inches}"`;
}

// Resolved membership item for rendering
export type ResolvedItem =
  | {
      type: "field";
      field: MeasurementField;
      membership: MeasurementGroupField;
    }
  | { type: "section-header"; groupName: string; groupColor: string | null };

interface ResolveOptions {
  /** Include section headers for child groups (default: true) */
  includeSectionHeaders?: boolean;
  /** Exclude derived/calculated fields (default: false) */
  excludeDerived?: boolean;
}

/**
 * Recursively resolve group memberships into a flat list with section headers.
 * Unified traversal used by group-card, record-dialog, and measurements-section.
 */
export function resolveGroupMemberships(
  group: MeasurementGroupWithFields,
  options?: ResolveOptions,
  visited = new Set<string>(),
): ResolvedItem[] {
  const items: ResolvedItem[] = [];
  if (visited.has(group.id)) return items;
  visited.add(group.id);

  const includeSectionHeaders = options?.includeSectionHeaders ?? true;
  const excludeDerived = options?.excludeDerived ?? false;

  const sorted = [...group.fieldMemberships].sort(
    (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0),
  );

  for (const m of sorted) {
    if (m.field) {
      if (excludeDerived && m.field.isDerived) continue;
      items.push({ type: "field", field: m.field, membership: m });
    } else if (m.childGroup) {
      if (includeSectionHeaders) {
        items.push({
          type: "section-header",
          groupName: m.childGroup.name,
          groupColor: m.childGroup.color ?? null,
        });
      }
      items.push(...resolveGroupMemberships(m.childGroup, options, visited));
    }
  }
  return items;
}

/**
 * Build a field map from group memberships (recursive).
 */
export function buildFieldMap(
  group: MeasurementGroupWithFields,
  visited = new Set<string>(),
): Map<string, MeasurementField> {
  const map = new Map<string, MeasurementField>();
  if (visited.has(group.id)) return map;
  visited.add(group.id);

  for (const m of group.fieldMemberships) {
    if (m.field) {
      map.set(m.field.id, m.field);
    } else if (m.childGroup) {
      const childMap = buildFieldMap(m.childGroup, visited);
      childMap.forEach((field, id) => map.set(id, field));
    }
  }
  return map;
}

/**
 * Recursively collect all field IDs from a group (including nested child groups).
 */
export function resolveGroupFieldIds(
  group: MeasurementGroupWithFields,
  visited = new Set<string>(),
): Set<string> {
  const fieldIds = new Set<string>();
  if (visited.has(group.id)) return fieldIds;
  visited.add(group.id);

  for (const m of group.fieldMemberships) {
    if (m.field) {
      fieldIds.add(m.field.id);
    } else if (m.childGroup) {
      resolveGroupFieldIds(m.childGroup, visited).forEach((id) =>
        fieldIds.add(id),
      );
    }
  }
  return fieldIds;
}

/**
 * Sort dates descending (newest first).
 */
export function sortedDates(
  valuesByDate: Record<string, MeasurementValueSummary[]>,
): string[] {
  return Object.keys(valuesByDate).sort(
    (a, b) => new Date(b).getTime() - new Date(a).getTime(),
  );
}

/**
 * Group measurement values by local date key.
 */
export function groupValuesByDate(
  values: MeasurementValueSummary[],
  timeZone?: string,
): Record<string, MeasurementValueSummary[]> {
  const byDate: Record<string, MeasurementValueSummary[]> = {};
  for (const v of values) {
    const dateKey = v.measurementDate
      ? zonedDateKey(v.measurementDate, timeZone ?? getTimezone())
      : "unknown";
    if (!byDate[dateKey]) byDate[dateKey] = [];
    byDate[dateKey].push(v);
  }
  return byDate;
}

/**
 * Parse a timestamp string to epoch millis, or null when absent/unparseable.
 */
export function parseTimestamp(
  value: string | null | undefined,
): number | null {
  if (!value) return null;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? null : time;
}

/**
 * The instant a value was recorded, falling back to its creation time.
 */
export function getValueTime(value: MeasurementValueSummary): number {
  return (
    parseTimestamp(value.measurementDate) ??
    parseTimestamp(value.createdDate) ??
    0
  );
}

/**
 * Order values oldest-first by (measurementDate, createdDate, id) so a newer
 * entry wins latest-per-field dedup even when measurementDate ties — ids are
 * prefixed+monotonic, so they order insertions.
 */
export function compareValuesAscending(
  a: MeasurementValueSummary,
  b: MeasurementValueSummary,
): number {
  const measurementTime = getValueTime(a) - getValueTime(b);
  if (measurementTime !== 0) return measurementTime;

  const createdTime =
    (parseTimestamp(a.createdDate) ?? 0) - (parseTimestamp(b.createdDate) ?? 0);
  if (createdTime !== 0) return createdTime;

  return a.id.localeCompare(b.id);
}

/**
 * Deduplicate values within each date so only the latest value per field is kept.
 */
export function deduplicateValuesByDate(
  valuesByDate: Record<string, MeasurementValueSummary[]>,
): Record<string, MeasurementValueSummary[]> {
  const result: Record<string, MeasurementValueSummary[]> = {};
  for (const [date, values] of Object.entries(valuesByDate)) {
    const sorted = [...values].sort(compareValuesAscending);
    const byField = new Map<string, MeasurementValueSummary>();
    for (const v of sorted) {
      byField.set(v.fieldId, v);
    }
    result[date] = Array.from(byField.values());
  }
  return result;
}

/**
 * Step size for numeric inputs based on data type and decimal places.
 */
export function getStepForField(
  dataType: string,
  decimalPlaces?: number | null,
): string {
  if (dataType === "INTEGER") return "1";
  if (decimalPlaces != null && decimalPlaces >= 0) {
    return decimalPlaces === 0
      ? "1"
      : (1 / Math.pow(10, decimalPlaces)).toString();
  }
  return "0.1";
}

/**
 * Format goal display text.
 */
export function formatGoalText(
  goal: MeasurementGoal,
  unit?: string | null,
  decimalPlaces?: number | null,
): string {
  const u = unit ? ` ${unit}` : "";
  const dp = decimalPlaces ?? 1;
  if (goal.isComputed) {
    return `Goal: ${goal.goalValue.toFixed(dp)}${u}`;
  }
  const label = goal.label || "Goal";
  const fmt = (n: number) => parseFloat(n.toFixed(dp));
  if (goal.goalValue != null) {
    return `${label}: ${fmt(goal.goalValue)}${u}`;
  }
  if (goal.goalMin != null && goal.goalMax != null) {
    return `${label}: ${fmt(goal.goalMin)}\u2013${fmt(goal.goalMax)}${u}`;
  }
  if (goal.goalMin != null) {
    return `${label}: \u2265${fmt(goal.goalMin)}${u}`;
  }
  if (goal.goalMax != null) {
    return `${label}: \u2264${fmt(goal.goalMax)}${u}`;
  }
  return "";
}

/**
 * Build a map of fieldId -> latest goal from values' embedded goal data.
 * Iterates values by date (newest first) and keeps the first goal found per field.
 */
export function latestGoalsFromValues(
  valuesByDate: Record<string, MeasurementValueSummary[]>,
): Map<string, MeasurementGoal> {
  const map = new Map<string, MeasurementGoal>();
  const sortedKeys = Object.keys(valuesByDate).sort(
    (a, b) => new Date(b).getTime() - new Date(a).getTime(),
  );
  for (const dateKey of sortedKeys) {
    for (const v of valuesByDate[dateKey]) {
      if (v.goal && !map.has(v.fieldId)) {
        map.set(v.fieldId, v.goal);
      }
    }
  }
  return map;
}

/**
 * Compute how far the current value is from the goal.
 * Returns a human-readable string like "10.5 to go", "3.2 until goal", "In range", etc.
 * Returns null if insufficient data.
 */
export function formatGoalDistance(
  goal: MeasurementGoal,
  currentValue: number | null | undefined,
  decimalPlaces?: number | null,
): string | null {
  if (currentValue == null) return null;
  const dp = decimalPlaces ?? 1;
  const round = (n: number) => parseFloat(n.toFixed(dp));

  if (goal.goalValue != null) {
    const diff = round(Math.abs(currentValue - goal.goalValue));
    if (diff === 0) return "At goal";
    if (currentValue > goal.goalValue) return `${diff} until goal`;
    return `${diff} to go`;
  }

  if (!goal.isComputed) {
    const { goalMin, goalMax } = goal;
    if (goalMin != null && goalMax != null) {
      if (currentValue < goalMin) {
        const diff = round(goalMin - currentValue);
        return `${diff} below range`;
      }
      if (currentValue > goalMax) {
        const diff = round(currentValue - goalMax);
        return `${diff} above range`;
      }
      return "In range";
    }
  }

  return null;
}

/**
 * Build a simple field map from a flat array of fields.
 * For group-based field maps (recursive), use `buildFieldMap` instead.
 */
export function createFieldMap(
  fields: MeasurementField[],
): Map<string, MeasurementField> {
  return new Map(fields.map((f) => [f.id, f]));
}

/**
 * Find the most recent numeric value for `fieldId` recorded on a day strictly
 * before `beforeDate`'s, both days read on `clock` — the clock `beforeDate`
 * was built on. Matches the row-delta semantics of the table view in
 * group-card.tsx, which compares each row against the latest entry from the
 * immediately prior day (same-day entries are deduped).
 */
export function findLatestPriorValue(
  values: MeasurementValueSummary[],
  fieldId: string,
  beforeDate: Date,
  clock: ClinicClock,
): MeasurementValueSummary | null {
  const cutoffDayKey = clock.dayOf(beforeDate);
  let best: MeasurementValueSummary | null = null;
  let bestTime = -Infinity;
  for (const v of values) {
    if (v.fieldId !== fieldId) continue;
    if (v.numericValue == null || !v.measurementDate) continue;
    if (clock.dayOf(v.measurementDate) >= cutoffDayKey) continue;
    const t = new Date(v.measurementDate).getTime();
    if (t > bestTime) {
      best = v;
      bestTime = t;
    }
  }
  return best;
}

/**
 * Compute total change (last - first) for a given field across sorted dates.
 * Returns null if fewer than 2 numeric values exist.
 */
export function computeTotalChange(
  valuesByDate: Record<string, MeasurementValueSummary[]>,
  dates: string[],
  fieldId: string,
): number | null {
  const allVals = dates
    .map((d) => valuesByDate[d].find((v) => v.fieldId === fieldId))
    .filter(
      (v): v is MeasurementValueSummary => v != null && v.numericValue != null,
    )
    .reverse(); // oldest first
  if (allVals.length < 2) return null;
  return allVals[allVals.length - 1].numericValue! - allVals[0].numericValue!;
}
